import { Pressable, StyleSheet, View } from 'react-native';
import {
  BookOpenCheck,
  Brain,
  CalendarDays,
  Camera,
  CheckCircle2,
  Edit3,
  Languages,
  Target,
} from 'lucide-react-native';
import { tokens } from '../../theme/tokens';
import { AppText } from '../ui/AppText';
import { ScreenBackground } from '../ui/ScreenBackground';

type FigmaHomeSurfaceProps = {
  gradeLabel: string;
  weakPointLabel: string;
  onPressGrade: () => void;
  onPressTopCamera: () => void;
  onPressPhotoSearch: () => void;
  onPressPracticeReview: () => void;
  onPressDedicatedExplain: () => void;
  onPressWrongBook: () => void;
  onPressSpecialPractice: () => void;
  onPressTranslation: () => void;
  onPressComposition: () => void;
};

type MainActionProps = {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  tone: 'primary' | 'success';
  onPress: () => void;
};

type ToolboxItemProps = {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  onPress: () => void;
};

export function FigmaHomeSurface({
  gradeLabel,
  weakPointLabel,
  onPressGrade,
  onPressTopCamera,
  onPressPhotoSearch,
  onPressPracticeReview,
  onPressDedicatedExplain,
  onPressWrongBook,
  onPressSpecialPractice,
  onPressTranslation,
  onPressComposition,
}: FigmaHomeSurfaceProps) {
  return (
    <View style={styles.phoneSurface}>
      <ScreenBackground style={styles.phoneBackground} contentStyle={styles.phoneContent}>
      <View style={styles.statusBar}>
        <AppText variant="body" weight="bold">
          9:41
        </AppText>
        <View style={styles.statusIcons}>
          <View style={styles.signalBars}>
            <View style={[styles.signalBar, styles.signalOne]} />
            <View style={[styles.signalBar, styles.signalTwo]} />
            <View style={[styles.signalBar, styles.signalThree]} />
          </View>
          <AppText variant="caption" weight="bold">
            ︶
          </AppText>
          <View style={styles.battery}>
            <View style={styles.batteryFill} />
          </View>
        </View>
      </View>

      <View style={styles.topNav}>
        <Pressable accessibilityRole="button" onPress={onPressGrade} style={styles.gradePill}>
          <AppText variant="body" tone="primary" weight="bold">
            {gradeLabel}⌄
          </AppText>
        </Pressable>
        <Pressable accessibilityRole="button" onPress={onPressTopCamera} style={styles.topCameraButton}>
          <Camera size={21} color={tokens.colors.text} />
        </Pressable>
      </View>

      <View style={styles.mainGrid}>
        <MainActionCard
          title="拍照搜题"
          subtitle="AI互动讲解"
          tone="primary"
          icon={<Camera size={26} color={tokens.colors.white} />}
          onPress={onPressPhotoSearch}
        />
        <MainActionCard
          title="练习回顾"
          subtitle="AI分析薄弱点"
          tone="success"
          icon={<CheckCircle2 size={26} color={tokens.colors.white} />}
          onPress={onPressPracticeReview}
        />
      </View>

      <View style={styles.promptCard}>
        <View style={styles.promptIcon}>
          <Target size={21} color={tokens.colors.danger} />
        </View>
        <View style={styles.promptCopy}>
          <AppText variant="caption" tone="primary">
            {weakPointLabel}需巩固，已为你准备好专项练习
          </AppText>
        </View>
        <Pressable accessibilityRole="button" onPress={onPressSpecialPractice} style={styles.startButton}>
          <AppText variant="caption" weight="bold" style={styles.startButtonText}>
            Start
          </AppText>
        </Pressable>
      </View>

      <AppText variant="subtitle" weight="bold" style={styles.sectionTitle}>
        百宝箱
      </AppText>

      <View style={styles.toolboxGrid}>
        <ToolboxItem
          title="专属讲解"
          subtitle="个性化AI讲解"
          icon={<Brain size={22} color={tokens.colors.primary} />}
          onPress={onPressDedicatedExplain}
        />
        <ToolboxItem
          title="AI错题本"
          subtitle="精准查漏补缺"
          icon={<BookOpenCheck size={22} color={tokens.colors.success} />}
          onPress={onPressWrongBook}
        />
        <ToolboxItem
          title="专项练习"
          subtitle="围绕薄弱点"
          icon={<CalendarDays size={22} color={tokens.colors.warning} />}
          onPress={onPressSpecialPractice}
        />
        <ToolboxItem
          title="拍照翻译"
          subtitle="敬请期待"
          icon={<Languages size={22} color={tokens.colors.primary} />}
          onPress={onPressTranslation}
        />
        <ToolboxItem
          title="语文作文"
          subtitle="敬请期待"
          icon={<Edit3 size={22} color={tokens.colors.warning} />}
          onPress={onPressComposition}
        />
      </View>
      </ScreenBackground>
    </View>
  );
}

function MainActionCard({ title, subtitle, icon, tone, onPress }: MainActionProps) {
  const isPrimary = tone === 'primary';
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.mainAction,
        isPrimary ? styles.primaryAction : styles.successAction,
        pressed ? styles.pressed : null,
      ]}
    >
      <View style={styles.mainActionCopy}>
        <AppText variant="subtitle" weight="bold" style={styles.mainActionTitle}>
          {title}
        </AppText>
        <AppText variant="caption" style={styles.mainActionSubtitle}>
          {subtitle}
        </AppText>
      </View>
      <View style={styles.mainActionIcon}>{icon}</View>
    </Pressable>
  );
}

function ToolboxItem({ title, subtitle, icon, onPress }: ToolboxItemProps) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [styles.toolboxItem, pressed ? styles.pressed : null]}
    >
      <View style={styles.toolboxIcon}>{icon}</View>
      <AppText variant="body" weight="bold" align="center">
        {title}
      </AppText>
      <AppText variant="caption" tone="muted" align="center">
        {subtitle}
      </AppText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  phoneSurface: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: tokens.layout.screenWidth,
    minHeight: 780,
    borderTopLeftRadius: 34,
    borderTopRightRadius: 34,
    overflow: 'hidden',
    backgroundColor: tokens.colors.background,
  },
  phoneBackground: {
    flex: 1,
  },
  phoneContent: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: tokens.spacing.xl,
  },
  statusBar: {
    height: 30,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
  },
  statusIcons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  signalBars: {
    height: 14,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
  signalBar: {
    width: 3,
    borderRadius: 2,
    backgroundColor: tokens.colors.text,
  },
  signalOne: {
    height: 5,
  },
  signalTwo: {
    height: 8,
  },
  signalThree: {
    height: 11,
  },
  battery: {
    width: 25,
    height: 12,
    borderWidth: 1.5,
    borderColor: tokens.colors.text,
    borderRadius: 3,
    padding: 1,
  },
  batteryFill: {
    flex: 1,
    borderRadius: 2,
    backgroundColor: tokens.colors.text,
  },
  topNav: {
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: tokens.border.width,
    borderBottomColor: tokens.colors.border,
  },
  gradePill: {
    minHeight: 34,
    borderRadius: tokens.radius.full,
    paddingHorizontal: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  topCameraButton: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  mainGrid: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  mainAction: {
    flex: 1,
    minHeight: 140,
    borderRadius: tokens.radius.lg,
    padding: 14,
    justifyContent: 'space-between',
    shadowColor: tokens.colors.text,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 14,
    elevation: 4,
  },
  primaryAction: {
    backgroundColor: tokens.colors.primary,
  },
  successAction: {
    backgroundColor: tokens.colors.success,
  },
  mainActionCopy: {
    gap: 2,
  },
  mainActionTitle: {
    color: tokens.colors.white,
  },
  mainActionSubtitle: {
    color: tokens.colors.white,
    opacity: 0.92,
  },
  mainActionIcon: {
    alignSelf: 'flex-end',
    width: 48,
    height: 48,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.22)',
  },
  promptCard: {
    minHeight: 56,
    borderRadius: 16,
    marginTop: 16,
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: tokens.colors.warningSoft,
  },
  promptIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  promptCopy: {
    flex: 1,
  },
  startButton: {
    minHeight: 34,
    minWidth: 62,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.success,
  },
  startButtonText: {
    color: tokens.colors.white,
  },
  sectionTitle: {
    marginTop: 20,
    marginBottom: 12,
  },
  toolboxGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  toolboxItem: {
    width: '48%',
    minHeight: 112,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    gap: 5,
    backgroundColor: tokens.colors.surface,
    shadowColor: tokens.colors.text,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 4,
  },
  toolboxIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  pressed: {
    opacity: 0.86,
  },
});
